/** Formatting helpers for the monitoring pages. */

/** Seconds -> "H:MM:SS" (or "MM:SS" under an hour). */
export const formatDuration = (totalSeconds) => {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};

/** Seconds -> "6h 12m", for at-a-glance worked totals. */
export const formatWorked = (totalSeconds) => {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0 && m === 0) return "—";
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
};

export const formatBytes = (bytes) => {
  const b = Number(bytes) || 0;
  if (!b) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
  const value = b / 1024 ** i;
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
};

/** Local wall-clock time, e.g. "14:32". */
export const formatTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString()} ${formatTime(dateStr)}`;
};

/** Today in YYYY-MM-DD, for date inputs. */
export const todayStr = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const daysAgoStr = (days) => {
  const d = new Date(Date.now() - days * 86400000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** An employee document may be populated or a bare id. */
export const employeeName = (employee) => {
  if (!employee || typeof employee !== "object") return "Unknown";
  return employee.fullNameEn || employee.fullNameAr || "Unknown";
};

export const employeeInitials = (employee) => {
  const name = employeeName(employee);
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
};

export const SESSION_STATUS_STYLE = {
  active: {
    label: "Active",
    color: "text-green-600 dark:text-green-400",
    bg: "bg-green-100 dark:bg-green-900/30",
  },
  paused: {
    label: "Paused",
    color: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-100 dark:bg-amber-900/30",
  },
  ended: {
    label: "Ended",
    color: "text-gray-600 dark:text-light-400",
    bg: "bg-gray-100 dark:bg-dark-200",
  },
  interrupted: {
    label: "Interrupted",
    color: "text-red-600 dark:text-red-400",
    bg: "bg-red-100 dark:bg-red-900/30",
  },
};

export const CAPTURE_MODE_LABEL = {
  screenshots: "Screenshots",
  video: "Video",
  both: "Video + screenshots",
  off: "Off",
};

/**
 * Storage/bandwidth estimate shown on the settings page.
 *
 * Grounded in MEASURED figures from real capture rather than theory:
 *   - a 720p WebP still averaged ~18 KB
 *   - 720p video at 1200 kbps averaged ~2.2 MB per 30s chunk (~590 kbps effective)
 *
 * The video number is an upper bound: videoBitsPerSecond is only advisory to the
 * encoder, and screen content usually undershoots it.
 */
export const estimateDailyBytes = ({
  captureMode,
  screenshotIntervalSeconds,
  videoBitrateKbps,
  hoursPerDay = 8,
}) => {
  const seconds = hoursPerDay * 3600;
  let bytes = 0;

  if (captureMode === "screenshots" || captureMode === "both") {
    const shots = seconds / Math.max(5, screenshotIntervalSeconds || 60);
    bytes += shots * 18 * 1024;
  }
  if (captureMode === "video" || captureMode === "both") {
    // Effective bitrate observed at roughly half the configured target.
    bytes += (seconds * (videoBitrateKbps || 0) * 1000 * 0.5) / 8;
  }
  return bytes;
};

export const estimateMonthlyBytes = (opts, workingDays = 22) =>
  estimateDailyBytes(opts) * workingDays;
